<template>
    <div
        class="search-dialog-backdrop"
        @click="handleClickOutside"
        v-show="isDialogOpen"
    >
        <div class="search-dialog" @click.stop>
            <div class="search-dialog-header">
                <global-search-tab-list
                    :tabs="tabs"
                    :active-tab-index="activeTabIndex"
                    @tab-click="updateActiveTab"
                />

                <div class="search-input-group" role="search">
                    <span><i class="fas fa-search"></i></span>
                    <input
                        ref="searchInput"
                        v-model="searchInput"
                        type="search"
                        placeholder="Search..."
                        aria-label="Search"
                    />
                </div>
            </div>

            <div
                v-if="activeTabIndex !== null"
                :id="currentTab.tabPanelId"
                class="tab-panel"
                role="tabpanel"
                :aria-labelledby="currentTab.tabId"
            >
                <component
                    :is="currentTab.tabPanelFilter"
                    v-if="searchInput !== '' && currentTab.tabPanelFilter"
                    class="search-filter"
                />

                <div class="content-section">
                    <keep-alive
                        :include="[
                            'TabPanelSchedule',
                            'TabPanelPatient',
                            'TabPanelClient',
                            'TabPanelContact',
                            'TabPanelInventory',
                            'TabPanelBilling'
                        ]"
                    >
                        <component
                            :is="currentTab.tabPanel"
                            :label="currentTab.tabLabel"
                            :search-input="searchInput"
                            @result-click="handleResultClick"
                        />
                    </keep-alive>

                    <tab-panel-recent-log
                        v-show="searchInput.trim() === ''"
                        @result-click="handleResultClick"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TabPanelSchedule from "@/module/layout/global-search/tab-panel-schedule.vue";
import TabPanelPatient from "@/module/layout/global-search/tab-panel-patient.vue";
import TabPanelClient from "@/module/layout/global-search/tab-panel-client.vue";
import TabPanelContact from "@/module/layout/global-search/tab-panel-contact.vue";
import TabPanelInventory from "@/module/layout/global-search/tab-panel-inventory.vue";
import TabPanelBilling from "@/module/layout/global-search/tab-panel-billing.vue";
import InputSearchIcon from "@/module/commons/input-search-icon.vue";
import TabPanelRecentLog from "@/module/layout/global-search/tab-panel-recent-log.vue";
import TabPanelPatientFilter from "@/module/layout/global-search/tab-panel-patient-filter.vue";
import GlobalSearchTabList from "@/module/layout/global-search/tab-list.vue";

export default {
    components: {
        GlobalSearchTabList,
        TabPanelPatientFilter,
        TabPanelRecentLog,
        InputSearchIcon
    },
    data() {
        return {
            tabs: [
                {
                    tabLabel: "Schedule",
                    tabId: "tab-schedule",
                    tabPanel: TabPanelSchedule,
                    tabPanelId: "tab-panel-schedule",
                    parentPath: "schedule"
                },
                {
                    tabLabel: "Patients",
                    tabPanel: TabPanelPatient,
                    tabId: "tab-patient",
                    tabPanelId: "tab-panel-patient",
                    tabPanelFilter: TabPanelPatientFilter,
                    parentPath: "patient"
                },
                {
                    tabLabel: "Clients",
                    tabPanel: TabPanelClient,
                    tabId: "tab-client",
                    tabPanelId: "tab-panel-client",
                    parentPath: "client"
                },
                {
                    tabLabel: "Contacts",
                    tabPanel: TabPanelContact,
                    tabId: "tab-contact",
                    tabPanelId: "tab-panel-contact",
                    parentPath: "contact"
                },
                {
                    tabLabel: "Inventory",
                    tabPanel: TabPanelInventory,
                    tabId: "tab-inventory",
                    tabPanelId: "tab-panel-inventory",
                    parentPath: "inventory"
                },
                {
                    tabLabel: "Billing",
                    tabPanel: TabPanelBilling,
                    tabId: "tab-billing",
                    tabPanelId: "tab-panel-billing",
                    parentPath: "billing"
                }
            ],
            activeTabIndex: null,
            searchInput: ""
        };
    },
    computed: {
        currentTab() {
            return this.tabs[this.activeTabIndex];
        },
        isSideMenuHidden() {
            return this.$store.state.isSideMenuHidden;
        },
        isDialogOpen() {
            return this.$store.state.isGlobalSearchDialogOpen;
        }
    },
    watch: {
        $route() {
            this.initActiveTab();
        },
        isDialogOpen(newVal) {
            if (newVal) {
                this.focusInput();
                document.addEventListener("keydown", this.handleEsc);
            } else {
                document.removeEventListener("keydown", this.handleEsc);
            }
        }
    },
    mounted() {
        this.initActiveTab();
    },
    methods: {
        initActiveTab() {
            const tabIndex = this.tabs.findIndex(tab =>
                this.$router.currentRoute.path.includes(tab.parentPath)
            );

            if (tabIndex >= 0) {
                this.activeTabIndex = tabIndex;
            } else {
                this.activeTabIndex = 0;
            }
        },
        updateActiveTab(index) {
            this.activeTabIndex = index;
            this.focusInput();
        },
        focusInput() {
            this.$nextTick(() => {
                this.$refs.searchInput.focus();
            });
        },
        handleResultClick() {
            this.searchInput = "";
            this.$store.commit("toggleGlobalSearchDialog", false);

            const mq = matchMedia("(max-width: 767px)");
            if (mq.matches && !this.isSideMenuHidden) {
                this.$store.commit("toggleSideMenu");
            }
        },
        handleClickOutside() {
            this.$store.commit("toggleGlobalSearchDialog", false);
        },
        handleEsc(event) {
            if (event.key === "Escape") {
                event.preventDefault();
                this.$store.commit("toggleGlobalSearchDialog", false);
                document.removeEventListener("keydown", this.handleEsc);
            }
        }
    },
    beforeDestroy() {
        this.$store.commit("toggleGlobalSearchDialog", false);

        document.removeEventListener("keydown", this.handleEsc);
    }
};
</script>

<style lang="scss" scoped>
$vet-text-subtlest: #626f86;
$vet-text-green: #527a22;
$vet-border: #091e4224;
$vet-border-focused: #388bff;
$top-bar-secondary-height: 52px;

.search-dialog-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1040;
}

.search-dialog {
    position: absolute;
    inset: ($top-bar-secondary-height - 4px) 16px 12px;
    height: 560px;
    border-radius: 6px;

    display: flex;
    flex-direction: column;
    box-shadow: var(
        --ds-shadow-overlay,
        0 4px 8px rgba(9, 30, 66, 0.25),
        0 0 1px rgba(9, 30, 66, 0.31)
    );
    background: #fff;

    .search-dialog-header {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 10px 20px 8px;
    }

    .search-input-group {
        position: relative;

        span {
            position: absolute;
            color: $vet-text-subtlest;
            top: 10px;
            left: 10px;
            height: 16px;
            width: 16px;
            text-align: center;
        }

        input[type="search"] {
            border-radius: 6px;
            border: 1px solid #091e4236;
            height: 36px;
            width: 100%;
            padding: 0 8px 0 32px;
            line-height: 36px;

            &:focus {
                outline: 2px solid $vet-border-focused;
                outline-offset: -2px;
            }
        }
    }

    .tab-panel {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        overflow-y: hidden;

        .content-section {
            padding: 8px 0;
            overflow-y: scroll;
            flex-grow: 1;
        }
    }
}

@media screen and (min-width: 768px) {
    .search-dialog {
        width: 640px;
        left: unset;
        right: 20px;

        .tab-panel {
            flex-direction: row-reverse;
        }
    }

    .search-filter {
        position: relative;

        &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            width: 1px;
            background-color: $vet-border;
            margin: 8px 0;
        }
    }
}

@media screen and (min-width: 1400px) {
    .search-dialog {
        right: 220px;
    }
}
</style>
